import { useCallback, useEffect, useState } from "react";
import { Trash2 } from "lucide-react";

import { api, errMsg } from "../../lib/api.js";
import { logError } from "../../lib/logger.js";
import { usePageBack } from "../../context/PageBackContext.js";
import { useToast } from "../../context/ToastContext.js";
import { useConfirm } from "../../components/ui/confirm-dialog.js";
import { AsyncButton } from "../../components/ui/async-button.js";
import { Button } from "../../components/ui/button.js";
import { Input } from "../../components/ui/input.js";
import { Label } from "../../components/ui/label.js";
import { Badge } from "../../components/ui/badge.js";
import { Checkbox } from "../../components/ui/checkbox.js";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card.js";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table.js";
import { ISSUE_STATUS_LABELS, ISSUE_STATUS_TONES } from "../../lib/issueStatus.js";

interface Participant {
  id: string;
  email: string;
  name: string;
  userId: string | null;
  isActive: boolean;
  canBeAssigned: boolean;
  notifyNewIssue: boolean;
  notifyReopened: boolean;
  notifyAssigned: boolean;
  notifyStatusChange: boolean;
  notifyComment: boolean;
}

type Flag = "isActive" | "canBeAssigned" | "notifyNewIssue" | "notifyReopened" | "notifyAssigned" | "notifyStatusChange" | "notifyComment";

const FLAG_COLUMNS: { key: Flag; label: string; hint: string }[] = [
  { key: "isActive", label: "Active", hint: "Off pauses every email and removes triage rights without deleting the person." },
  { key: "canBeAssigned", label: "Assignable", hint: "Shows in the Assignee picker on issues." },
  { key: "notifyNewIssue", label: "New issue", hint: "Email when anyone logs a bug or issue." },
  { key: "notifyReopened", label: "Reopened", hint: "Email when an issue is reopened." },
  { key: "notifyAssigned", label: "Assigned", hint: "Email on every assignment (the assignee always gets theirs)." },
  { key: "notifyStatusChange", label: "Status", hint: "Email when an issue moves to Working, Deferred, Not a bug and so on." },
  { key: "notifyComment", label: "Comments", hint: "Email on every comment." },
];

const STATUS_HELP: { status: string; text: string }[] = [
  { status: "open", text: "New, nobody on it yet." },
  { status: "in_progress", text: "Assigned and being worked on." },
  { status: "needs_info", text: "Waiting on the reporter for details." },
  { status: "deferred", text: "Valid but parked, optionally until a date." },
  { status: "resolved", text: "Fixed by the assignee; the reporter is asked to verify." },
  { status: "closed", text: "Reporter (or admin) confirmed the fix." },
  { status: "reopened", text: "Reporter says it still fails; back to the team." },
  { status: "not_a_bug", text: "Works as designed. Needs a reason; closes the issue." },
  { status: "duplicate", text: "Already reported. Links the original; closes the issue." },
  { status: "wont_fix", text: "Valid, but won't be fixed. Needs a reason; closes the issue." },
];

/** Admin > Issue Tracker Settings: who triages issues and who gets the team emails. */
export default function IssueTrackerSettingsPage() {
  usePageBack("/admin", "Back to Admin");
  const { show: showToast } = useToast();
  const confirm = useConfirm();
  const [rows, setRows] = useState<Participant[] | null>(null);
  const [loadError, setLoadError] = useState("");
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [addError, setAddError] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get<Participant[]>("/admin/issue-participants");
      setRows(data);
      setLoadError("");
    } catch (err) {
      logError("IssueTrackerSettingsPage.load", err);
      setLoadError(errMsg(err) ?? "Could not load participants.");
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  async function toggle(p: Participant, key: Flag, value: boolean) {
    setRows(rs => rs?.map(r => (r.id === p.id ? { ...r, [key]: value } : r)) ?? rs);
    try {
      await api.patch(`/admin/issue-participants/${p.id}`, { [key]: value });
    } catch (err) {
      logError("IssueTrackerSettingsPage.toggle", err, { key });
      showToast(errMsg(err) ?? "Could not save the change.", "error");
      await load();
    }
  }

  async function add() {
    setAddError("");
    if (!/^\S+@\S+\.\S+$/.test(email.trim())) { setAddError("Enter a valid email address."); throw new Error("validation"); }
    try {
      await api.post("/admin/issue-participants", { email: email.trim(), ...(name.trim() ? { name: name.trim() } : {}) });
      setEmail(""); setName("");
      showToast("Participant added", "success");
      await load();
    } catch (err) {
      logError("IssueTrackerSettingsPage.add", err);
      setAddError(errMsg(err) ?? "Could not add the participant.");
      throw err;
    }
  }

  async function remove(p: Participant) {
    const ok = await confirm({
      title: `Remove ${p.name}?`,
      description: "They stop getting issue emails and lose triage rights. Issues already assigned to them keep the assignee.",
      actionText: "Remove",
      tone: "destructive",
    });
    if (!ok) return;
    try {
      await api.delete(`/admin/issue-participants/${p.id}`);
      await load();
    } catch (err) {
      logError("IssueTrackerSettingsPage.remove", err);
      showToast(errMsg(err) ?? "Could not remove the participant.", "error");
    }
  }

  async function sendTest() {
    try {
      const { data } = await api.post<{ status: string; reason?: string | null; recipients: string[] }>("/admin/issue-participants/test-email");
      if (data.status === "SENT") showToast(`Test email sent to ${data.recipients.join(", ")}`, "success");
      else showToast(`Not sent (${data.status}): ${data.reason ?? "see the delivery log in Email Templates"}`, "error", 8000);
      if (data.status !== "SENT") throw new Error(data.status);
    } catch (err) {
      logError("IssueTrackerSettingsPage.sendTest", err);
      throw err;
    }
  }

  return (
    <div className="mx-auto flex max-w-6xl flex-col gap-5 p-6">
      <div>
        <h1 className="text-[22px] font-bold text-surface-900">Issue Tracker Settings</h1>
        <p className="text-[13px] text-surface-500">
          Engineers listed here can assign and triage issues, and get one shared email per event with everyone in To.
        </p>
      </div>

      <Card>
        <CardHeader className="flex flex-row flex-wrap items-start justify-between gap-3">
          <div>
            <CardTitle>Participants</CardTitle>
            <CardDescription>Tick which emails each person gets. Changes save immediately.</CardDescription>
          </div>
          <AsyncButton variant="outline" animated={false} onClick={() => sendTest()} disabled={!rows?.some(r => r.isActive)}>
            Send test email to all
          </AsyncButton>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          {loadError && <p className="text-sm text-error">{loadError}</p>}
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  {FLAG_COLUMNS.map(c => <TableHead key={c.key} className="text-center" title={c.hint}>{c.label}</TableHead>)}
                  <TableHead className="w-10" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows === null ? (
                  <TableRow><TableCell colSpan={FLAG_COLUMNS.length + 3} className="py-8 text-center text-surface-500">Loading…</TableCell></TableRow>
                ) : rows.length === 0 ? (
                  <TableRow><TableCell colSpan={FLAG_COLUMNS.length + 3} className="py-8 text-center text-surface-500">No participants yet. Add the engineer team below.</TableCell></TableRow>
                ) : rows.map(p => (
                  <TableRow key={p.id} className={p.isActive ? "" : "opacity-60"}>
                    <TableCell className="font-medium">
                      {p.name}
                      {!p.userId && <Badge tone="warning" className="ml-2" title="No HRMS login with this email, so they get emails but can't be assigned or triage in the app.">email only</Badge>}
                    </TableCell>
                    <TableCell className="text-surface-600">{p.email}</TableCell>
                    {FLAG_COLUMNS.map(c => (
                      <TableCell key={c.key} className="text-center">
                        <Checkbox
                          checked={p[c.key]}
                          onCheckedChange={v => void toggle(p, c.key, v === true)}
                          aria-label={`${c.label} for ${p.name}`}
                        />
                      </TableCell>
                    ))}
                    <TableCell>
                      <Button variant="ghost" size="icon" onClick={() => void remove(p)} aria-label={`Remove ${p.name}`}>
                        <Trash2 size={14} />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          <div className="flex flex-wrap items-end gap-2 border-t border-surface-200 pt-4">
            <div className="min-w-[240px] flex-1">
              <Label htmlFor="participant-email">Add by email</Label>
              <Input id="participant-email" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="name@integertel.com" />
            </div>
            <div className="min-w-[180px] flex-1">
              <Label htmlFor="participant-name">Name (optional — taken from HRMS when blank)</Label>
              <Input id="participant-name" value={name} onChange={e => setName(e.target.value)} />
            </div>
            <AsyncButton onClick={() => add()}>Add participant</AsyncButton>
          </div>
          {addError && <p className="text-sm text-error">{addError}</p>}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>How issue statuses work</CardTitle>
          <CardDescription>Participants change status from the issue drawer. Reporters can reopen anything closed without a fix.</CardDescription>
        </CardHeader>
        <CardContent>
          <ul className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {STATUS_HELP.map(s => (
              <li key={s.status} className="flex items-start gap-2 text-[13px] text-surface-600">
                <Badge tone={ISSUE_STATUS_TONES[s.status] ?? "neutral"} className="shrink-0">{ISSUE_STATUS_LABELS[s.status]}</Badge>
                {s.text}
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}
